import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { ArrowLeft, CreditCard, Loader2, Send, Square, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/payment-help")({
  head: () => ({
    meta: [
      { title: "Payment & Plans Help | Smart Word Labs" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Ask questions about your plan, billing and checkout." },
      { property: "og:title", content: "Payment & Plans Help | Smart Word Labs" },
      { property: "og:description", content: "Personal guidance on plans, billing and checkout." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PaymentHelpPage,
});

const SUGGESTIONS = [
  "Which plan suits my child best?",
  "When does my subscription renew?",
  "How do I cancel or change my card?",
  "What is your refund policy?",
];

function PaymentHelpPage() {
  const [initial, setInitial] = useState<UIMessage[] | null>(null);

  useEffect(() => {
    supabase
      .from("payment_assistant_messages")
      .select("message")
      .order("created_at", { ascending: true })
      .limit(200)
      .then(({ data, error }) => {
        if (error) toast.error("Couldn't load your previous conversation.");
        setInitial((data ?? []).map((r) => r.message as unknown as UIMessage));
      });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto flex h-screen max-w-3xl flex-col px-4 py-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Parent Portal
          </Link>
        </div>
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <CreditCard className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Payment & Plans Help</h1>
            <p className="text-sm text-muted-foreground">Answers based on your family's plan and bookings.</p>
          </div>
        </div>
        {initial === null ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Chat initial={initial} />
        )}
      </div>
    </div>
  );
}

function Chat({ initial }: { initial: UIMessage[] }) {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const { messages, sendMessage, status, stop, setMessages, error } = useChat({
    id: "payment-help",
    messages: initial,
    transport: new DefaultChatTransport({
      api: "/api/payment-assistant",
      headers: async (): Promise<Record<string, string>> => {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
    }),
    onError: (e) => toast.error(e.message || "Something went wrong."),
  });

  const busy = status === "submitted" || status === "streaming";

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, status]);
  useEffect(() => {
    if (!busy) inputRef.current?.focus();
  }, [busy]);

  const send = (text: string) => {
    const t = text.trim();
    if (!t || busy) return;
    setInput("");
    void sendMessage({ text: t });
  };

  const reset = async () => {
    const { data } = await supabase.auth.getUser();
    if (!data.user) return;
    const { error: delErr } = await supabase
      .from("payment_assistant_messages")
      .delete()
      .eq("user_id", data.user.id);
    if (delErr) {
      toast.error("Couldn't clear the conversation.");
      return;
    }
    setMessages([]);
    inputRef.current?.focus();
  };

  const last = messages[messages.length - 1];
  const waiting = status === "submitted" || (status === "streaming" && last?.role === "user");

  return (
    <div className="flex min-h-0 flex-1 flex-col rounded-2xl border border-border bg-card">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {messages.length === 0 && (
          <div className="space-y-3 py-6 text-center">
            <p className="text-sm text-muted-foreground">Ask anything about plans, prices, checkout or billing.</p>
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <Button key={s} variant="outline" size="sm" onClick={() => send(s)}>
                  {s}
                </Button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div
              className={
                m.role === "user"
                  ? "max-w-[85%] whitespace-pre-wrap rounded-2xl bg-primary px-4 py-2 text-sm text-primary-foreground"
                  : "max-w-[90%] whitespace-pre-wrap text-sm leading-relaxed text-foreground"
              }
            >
              {m.parts.map((p, i) => (p.type === "text" ? <span key={i}>{p.text}</span> : null))}
            </div>
          </div>
        ))}
        {waiting && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
          </div>
        )}
        {error && !busy && <p className="text-sm text-destructive">{error.message}</p>}
        <div ref={endRef} />
      </div>
      <form
        className="flex items-end gap-2 border-t border-border p-3"
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
      >
        <Textarea
          ref={inputRef}
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          placeholder="e.g. Can I switch from Cohort Starter to Private 1-on-1?"
          rows={2}
          className="min-h-[44px] resize-none"
        />
        {messages.length > 0 && !busy && (
          <Button type="button" variant="ghost" size="icon" onClick={reset} aria-label="New conversation">
            <RotateCcw className="h-4 w-4" />
          </Button>
        )}
        {busy ? (
          <Button type="button" size="icon" onClick={() => stop()} aria-label="Stop">
            <Square className="h-4 w-4" />
          </Button>
        ) : (
          <Button type="submit" size="icon" disabled={!input.trim()} aria-label="Send">
            <Send className="h-4 w-4" />
          </Button>
        )}
      </form>
    </div>
  );
}
